<template>
  <section
    :class="['pricing', (shoppingCart.length >= 2 || isPromoAvailable) && 'pricing--populated']"
    id="pricing"
  >
    <div class="pricing__title-wrapper">
      <h3 class="pricing__title">Cart</h3>
    </div>
    <div class="pricing__breakdown">
      <div v-if="subscriptionsList.length" class="pricing__products-section">
        <PricingListItem
          v-for="product in subscriptionsList"
          :key="product.id"
          :product="product"
        />
      </div>
      <div
        v-if="certificationsList.length"
        class="pricing__products-section pricing__products-section--certifications"
      >
        <PricingListItem
          v-for="product in certificationsList"
          :key="product.id"
          :product="product"
        />
      </div>
      <div
        v-if="expertEdgeEventsInCart.length"
        class="pricing__products-section pricing__products-section--expert-edge-events"
      >
        <PricingListItem
          v-for="product in expertEdgeEventsInCart"
          :key="product.id"
          :product="product"
        />
      </div>
      <div v-if="bootcampsInCart.length" class="pricing__products-section">
        <PricingListItem v-for="product in bootcampsInCart" :key="product.id" :product="product" />
      </div>
      <AddOns
        v-if="certificationsInAddOns.length"
        :certifications="certificationsInAddOns"
        :selectedPlanCode="selectedPlanCode"
        :isFreeTrial="isFreeTrial"
      />
      <div class="pricing__prices-wrapper">
        <template v-if="showCouponInput">
          <p class="pricing__type">Promo code</p>
          <Pill v-if="showCouponAppliedPill" info class="pricing__coupon-pill-wrapper">
            COUPON APPLIED
          </Pill>
          <form @submit.prevent="setupPricing" class="pricing__promo">
            <div v-if="canShowCoupon" class="pricing__coupon-wrapper">
              <div :class="['pricing__coupon', !isCouponValid && 'pricing__coupon--invalid']">
                <span>{{ coupon }}</span>
                <div class="pricing__coupon-close" @click.prevent="removeCoupon()"></div>
              </div>
              <div
                :class="[
                  'pricing__coupon-label',
                  !isCouponValid && 'pricing__coupon-label--invalid',
                ]"
              >
                <vue-loaders
                  v-if="!canShowCoupon && couponSpinner"
                  class="pricing__promo-input-spinner"
                  name="line-spin-fade-loader"
                  color="#dfe1e8"
                  scale=".3"
                ></vue-loaders>
                {{ couponLabel }}
              </div>
            </div>
            <div
              :class="[
                'pricing__promo-input-wrapper',
                canShowCoupon && 'pricing__promo-input-wrapper--hide',
              ]"
            >
              <input
                class="ine-input pricing__promo-input"
                type="text"
                placeholder="Enter Promo Code"
                v-model="coupon"
              />
              <vue-loaders
                v-if="!canShowCoupon && couponSpinner"
                class="pricing__promo-input-spinner"
                name="line-spin-fade-loader"
                color="#dfe1e8"
                scale=".3"
              ></vue-loaders>
            </div>
            <button
              type="submit"
              :class="[
                'btn',
                'btn--primary',
                'pricing__btn',
                canShowCoupon && 'pricing__btn--hide',
              ]"
              :disabled="!coupon"
              @click="addCoupon"
            >
              Apply
            </button>
          </form>
        </template>
        <p class="pricing__type">Total</p>
        <div class="pricing__total-group">
          <div class="pricing__line">
            <p class="pricing__label">subtotal</p>
            <p class="pricing__value">
              <span>{{ currencySymbol }}</span>
              <span>{{ subtotal }}</span>
            </p>
          </div>
          <div class="pricing__line">
            <p class="pricing__label">discount</p>
            <p class="pricing__value">
              <span>{{ currencySymbol }}</span>
              <span>{{ discount }}</span>
            </p>
          </div>
          <div class="pricing__line">
            <p class="pricing__label">tax</p>
            <p class="pricing__value">
              <span>{{
                taxes > 0 || isFreeTrial ? `${currencySymbol}${taxes}` : 'Not Included'
              }}</span>
            </p>
          </div>
          <div class="pricing__total">
            <p class="pricing__label pricing__label--total">
              {{ taxes > 0 || isFreeTrial ? 'Total due today' : 'Total before tax' }}
            </p>
            <p class="pricing__value pricing__value--total">
              <span>{{ currencySymbol }}</span>
              <span>{{ total }} USD</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { priceDetailUpdates, updateCart } from '@/helpers/mixins'
import PricingListItem from '@/components/cart/PricingListItem.vue'
import AddOns from '@/components/cart/AddOns.vue'
import Pill from '@/components/shared/Pill.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'PricingList',
  mixins: [priceDetailUpdates, updateCart],
  components: {
    PricingListItem,
    AddOns,
    Pill,
  },
  data() {
    return {
      couponSpinner: false,
      certificationsList: [],
      certificationsInAddOns: [],
      subscriptionsList: [],
    }
  },
  computed: {
    ...mapState({
      availableBootcamps: state => state.products.availableBootcamps,
      availableCertifications: state => state.products.availableCertifications,
      availablePlans: state => state.products.availablePlans,
      expertEdgeEvents: state => state.products.availableExpertEdgeEvents,
      selectedProductType: state => state.products.selectedProductType,
    }),
    ...mapGetters({
      isBusinessUser: 'business/isBusinessUser',
    }),
    CloseIconCoupon() {
      return CloseIconCoupon
    },
    selectedPlanName() {
      const name = this.selectedPlan?.name ?? ''
      return name.replace('INE', '')
    },
    isPremiumPlanSelected() {
      return this.selectedPlanCode === CONSTANTS.PLANS.PREMIUM
    },
    subscriptionsInCart() {
      const codes = this.availablePlans.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    certificationsInCart() {
      const codes = this.availableCertifications.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    bootcampsInCart() {
      const codes = this.availableBootcamps.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    expertEdgeEventsInCart() {
      const codes = this.expertEdgeEvents.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    showCouponAppliedPill() {
      return this.canShowCoupon && this.isCouponValid
    },
    isCouponValid() {
      return !this.couponError
    },
    canShowCoupon() {
      return !!this.currentCoupon && this.couponError !== null
    },
    couponLabel() {
      let couponLabel = null
      if (this.isCouponValid) {
        couponLabel = this.isFreeTrial
          ? `Nice! You're saving $${this.currentDiscount} on the first billing period after your trial ends.`
          : `Nice! You're saving $${this.currentDiscount}`
      } else {
        couponLabel = 'Code invalid'
      }
      return couponLabel
    },
    isCartFlowPage() {
      return ['payment', 'confirmation'].includes(this.$route.name)
    },
    showCouponInput() {
      return (
        !this.expertEdgeEventsInCart.length ||
        (this.expertEdgeEventsInCart.length && !this.isBusinessUser)
      )
    },
  },
  methods: {
    setSubscriptionsList() {
      const { PLANS } = CONSTANTS
      const codes = this.availablePlans.map(p => p.merchant_id)
      const subscriptionsList = []
      this.shoppingCart.forEach(i => {
        if (codes.includes(i.merchant_id) && i.merchant_id === PLANS.SKILL_DIVE_ANNUAL) {
          if (!i.selectedAsAddOns) subscriptionsList.push(i)
        } else if (codes.includes(i.merchant_id)) {
          subscriptionsList.push(i)
        }
      })
      this.subscriptionsList = subscriptionsList
    },
    setCertificationsList() {
      const { PLANS, VOUCHERS } = CONSTANTS
      const itemsId = this.shoppingCart.map(item => item.merchant_id)
      this.setCertificationsInAddOns()
      const addOnsItemsId = this.certificationsInAddOns.map(item => item.merchant_id)
      this.certificationsList = this.availableCertifications.filter(item => {
        if (this.selectedPlanCode === PLANS.ANNUAL && item.merchant_id === VOUCHERS.ICCA) {
          this.removeSelectedFromShoppingCart(item)
          return false
        }
        return this.isCartFlowPage
          ? itemsId.includes(item.merchant_id) && !item.addOn
          : itemsId.includes(item.merchant_id) && !addOnsItemsId.includes(item.merchant_id)
      })
    },
    setCertificationsInAddOns() {
      const { PLANS, VOUCHERS } = CONSTANTS
      const itemsId = this.shoppingCart.map(item => item.merchant_id)

      this.certificationsInAddOns = this.availableCertifications
        .filter(item => {
          item.included = false
          switch (item.merchant_id) {
            case VOUCHERS.ICCA:
              if ([PLANS.ANNUAL].includes(this.selectedPlanCode)) item.included = true
              break
            case VOUCHERS.EJPT:
              if (
                [PLANS.ANNUAL, PLANS.MONTHLY_EJPT, PLANS.QUARTERLY_EJPT].includes(
                  this.selectedPlanCode
                )
              )
                item.included = true
              break
            case VOUCHERS.EDA:
              if ([PLANS.EDA_PREMIUM].includes(this.selectedPlanCode)) item.included = true
              break
            default:
              break
          }
          if (
            !this.isCartFlowPage &&
            !this.shoppingCart.find(i => i.merchant_id === item.merchant_id)?.addOn
          )
            item.addOnChecked = false
          // if user has a freeTrial subscription on cart should not be able to buy VOUCHERS.EJPT
          if (this.isFreeTrial && [PLANS.PREMIUM, PLANS.MONTHLY].includes(this.selectedPlanCode)) {
            itemsId.push(VOUCHERS.EJPT)
          }
          return !itemsId.includes(item.merchant_id) || item.addOn
        })
        .reverse()
    },
  },
  watch: {
    shoppingCart(items) {
      this.setupPricing()
    },
    subtotal() {
      if (this.coupon.length > 0) {
        this.addCoupon()
      }
    },
    paymentEntries: {
      deep: true,
      handler(paymentEntries) {
        const { country, zip } = paymentEntries
        this.checkoutPricing
          .address({
            country,
            postal_code: zip,
          })
          .catch(err => this.handleError(err))
          .done()
      },
    },
    subscriptionsInCart() {
      this.setSubscriptionsList()
    },
    certificationsInCart() {
      this.setCertificationsList()
    },
  },
  created() {
    this.isPromoAvailable = this.coupon.length > 0
    this.setSubscriptionsList()
    this.setCertificationsList()
    this.setCertificationsInAddOns()
    this.setupPricing()
  },
}
</script>

<style lang="scss">
.pricing {
  height: 100%;
  width: 100%;
  margin: 0 auto;
  &__title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 20px 0;
  }
  &__title {
    color: var(--white);
    font-family: var(--font-regular);
    font-weight: 450;
    font-size: rem(24);
    line-height: 32px;
    margin: 0;
  }
  &__count-badge {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 9px 14px;
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 20px;
    text-transform: uppercase;
    font-size: rem(12);
    line-height: 14px;
    font-family: var(--font-regular);
  }
  &__breakdown {
    padding: 0;
  }
  &__type {
    font-family: var(--font-regular);
    color: var(--white);
    font-size: rem(20px);
    line-height: 26px;
    text-align: left;
    font-weight: 700;
    margin-bottom: 10px;
  }
  &__prices-wrapper {
    position: relative;
    margin: 20px 0 0 0;
    @include tablet {
      margin: 50px 0 0 0;
      margin: 0;
    }
  }
  &__line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin: 0 0 15px 0;
    &:last-child {
      margin: 0;
    }
  }
  &__label {
    font-family: var(--font-regular);
    text-transform: capitalize;
    font-size: rem(16);
    margin: 0;
    &--total {
      color: var(--white);
      text-transform: none;
      font-size: rem(18);
    }
  }
  &__value {
    font-family: var(--font-regular);
    font-size: rem(16);
    margin: 0;
    &--total {
      color: var(--white);
      text-transform: none;
      font-size: rem(16);
    }
  }
  &__total {
    font-weight: 500;
    font-size: rem(18px);
    line-height: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border-top: 1px solid #444444;
    padding-top: 5px;
  }
  &__promo {
    position: relative;
    font-family: var(--font-regular);
    outline: none;
    border: none;
    margin: 12.5px auto;
    padding: 0;
    width: 100%;
    display: flex;
    align-items: center;
    &-input {
      height: 46px;
      width: 100%;
      &-wrapper {
        width: 100%;
        &--hide {
          display: none;
        }
      }
      &-spinner {
        position: absolute;
        right: 100px;
        bottom: -3px;
      }
    }
  }
  &__input {
    background: #fff;
    padding: 5px;
    margin: 0;
    width: 100%;
    border-radius: 5px;
    font-size: 1rem;
    border: 1px solid var(--faded-gray);
    opacity: 0.7;
    font-family: var(--font-regular);
    &:focus {
      opacity: 1;
    }
  }
  &__btn {
    margin-left: 15px;
    &--hide {
      visibility: hidden;
    }
  }
  &__total-group {
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 10px;
    padding: 15px;
    color: var(--white);
  }
  &__coupon {
    height: 26px;
    padding: 5px 7px 5px 10px;
    color: var(--white);
    gap: 10px;
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--purple);

    &-wrapper {
      display: flex;
      align-items: center;
      width: 100%;
      height: 26px;
      min-width: fit-content;
    }
    &-label {
      color: var(--purple-pale);
      font-size: rem(14);
      line-height: 20px;
      margin-left: 10px;
      &--invalid {
        color: var(--performance-neg);
      }
    }
    &-close {
      cursor: pointer;
      width: 16px;
      height: 24px;
      // @/assets/icons/close_modal_x.svg
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18 6L6 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M6 6L18 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
      background-size: 18px 24px;
      background-repeat: no-repeat;
    }
    &--invalid {
      background: var(--performance-neg);
    }
  }
  &__coupon-pill-wrapper {
    position: absolute;
    top: 0;
    right: 0;
    color: var(--white);
    background: var(--purple-gradient);
  }
  &__products {
    &-section {
      display: flex;
      flex-flow: column;
      row-gap: 10px;
      &--certifications,
      &--expert-edge-events {
        margin-top: 10px;
        .pricing-list-item {
          &__wrapper {
            height: auto;
            align-items: center;
          }
          &__btn {
            align-self: auto;
          }
        }
      }
    }
  }
}
</style>
